import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Code, Zap, Shield, Globe, Terminal, Book, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

const codeExample = `// Exemple d'utilisation de l'API Floop
const response = await fetch('https://api.floop.ai/v1/generate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    prompt: "Un chat astronaute sur la lune",
    model: "floop-pro",
    width: 1024,
    height: 1024,
  }),
});

const data = await response.json();
console.log(data.image_url);`;

const features = [
  {
    icon: Zap,
    title: "Performance optimale",
    description: "Temps de réponse < 5 secondes pour la plupart des requêtes.",
  },
  {
    icon: Shield,
    title: "Sécurisé",
    description: "Authentification par API Key avec rate limiting intelligent.",
  },
  {
    icon: Globe,
    title: "REST API",
    description: "API RESTful standard, facile à intégrer dans n'importe quel projet.",
  },
  {
    icon: Code,
    title: "SDKs disponibles",
    description: "SDKs officiels pour JavaScript, Python, PHP et plus.",
  },
];

const Api = () => {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const handleWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      toast({
        title: "Inscription confirmée !",
        description: "Vous serez notifié dès le lancement de l'API.",
      });
      setEmail("");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-highlight/20 text-highlight rounded-full text-sm font-medium mb-6">
              <Bell className="w-4 h-4" />
              Coming Soon
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
              API{" "}
              <span className="bg-gradient-floop bg-clip-text text-transparent">
                Floop
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8">
              Intégrez la puissance de Floop directement dans vos applications. 
              Génération d'images, retouches et transformations via une API simple et performante.
            </p>

            {/* Waitlist Form */}
            <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-input border-border rounded-xl"
                required
              />
              <Button type="submit" className="bg-gradient-floop hover:opacity-90 text-white rounded-xl px-6">
                M'inscrire à la liste d'attente
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Code Preview */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-foreground">Exemple d'intégration</span>
            </div>
            <div className="bg-background border border-border rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-secondary/50 border-b border-border">
                <div className="w-3 h-3 rounded-full bg-destructive/50" />
                <div className="w-3 h-3 rounded-full bg-highlight/50" />
                <div className="w-3 h-3 rounded-full bg-success/50" />
                <span className="text-xs text-muted-foreground ml-2">api-example.js</span>
              </div>
              <pre className="p-6 overflow-x-auto text-sm">
                <code className="text-muted-foreground">{codeExample}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
              Conçue pour les développeurs
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Une API moderne, documentée et facile à utiliser.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
              Tarification flexible
            </h2>
            <p className="text-muted-foreground mb-8">
              Payez uniquement ce que vous utilisez. Pas de frais cachés.
            </p>
            <div className="bg-card border border-border rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-foreground mb-2">Floop Basic</div>
                  <div className="text-muted-foreground">À partir de</div>
                  <div className="text-2xl font-bold text-primary mt-2">0.01€ / image</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-foreground mb-2">Floop Pro</div>
                  <div className="text-muted-foreground">À partir de</div>
                  <div className="text-2xl font-bold text-primary mt-2">0.05€ / image</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                * Tarifs estimés. Prix définitifs au lancement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Documentation Preview */}
      <section className="py-16">
        <div className="container mx-auto px-6 text-center">
          <Book className="w-16 h-16 text-primary mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
            Documentation complète
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Guides détaillés, exemples de code et références API pour vous aider à démarrer rapidement.
          </p>
          <Button variant="outline" className="rounded-full px-6" disabled>
            Documentation (bientôt)
          </Button>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Api;
